import * as React from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  CmdCalendar,
  CmdInput,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdTextarea,
} from '@commander-services/gui-components';
import { useQueries } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { AxiosResponse } from 'axios';
import usePageWithForm from '../../../hooks/usePageWithForm';
import { IFuelCardsInput } from '../../FuelCards';
import SimpleSelectWithSearchHookForm from '../../Forms/Fields/SimpleSelectWithSearchHookForm';
import FuelCardsService from '../../FuelCards/FuelCardsService';
import FormikValidatorService from '../../../Services/FormikValidatorService';
import { getFuelCardFormData } from '../VehicleSettingsService';
import { transformErrors, validateFields } from '../../../Services/HookFormService';
import useAnalytics from '../../../hooks/useAnalytics';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ModalFormProps {
  customerName: string;
  handleNewFuelCard: (values) => void;
  handleClose: any;
  customerId: number;
  vehicleId: number;
}

interface IFuelCardsInputForm {
  fuelCardProviderId: string;
  cardNumber: string;
  identifier: string;
  validUntil: Date;
  note: string;
}

type TFuelCardsFormKeys =
  | 'fuelCardProviderId'
  | 'cardNumber'
  | 'identifier'
  | 'validUntil'
  | 'note';

export default function Form(props: ModalFormProps): JSX.Element {
  const handleInitDatepickerValue = () => {
    const date = new Date();
    const month = date.getMonth();
    const year = date.getFullYear();
    date.setMonth(month + 1);
    date.setFullYear(year + 2);
    return date;
  };

  usePageWithForm();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const { formatMessage: f } = useIntl();
  const [validation, setValidation] = React.useState<any>(null);
  const { trackEvent } = useAnalytics();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    trigger,
    watch,
    setFocus,
    clearErrors,
    formState: { errors },
  } = useForm<IFuelCardsInputForm>({
    mode: 'onChange',
    defaultValues: {
      fuelCardProviderId: '',
      cardNumber: '',
      identifier: '',
      validUntil: handleInitDatepickerValue(),
      note: '',
    },
    resolver: async (data) => {
      const validateErros = validateFields(data, validation);
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const handleFormData = (data: IFuelCardsInput[], type: string) => {
    const validUntil = data.find((i) => i.indexId === 'validUntil')?.[type];
    if (typeof validUntil === 'string') {
      setValue('validUntil', new Date(validUntil));
    }
    return {
      fuelCardProviderId: data.find((i) => i.indexId === 'fuelCardProviderId')?.[type],
      cardNumber: data.find((i) => i.indexId === 'cardNumber')?.[type],
      identifier: data.find((i) => i.indexId === 'identifier')?.[type],
      validUntil,
      customerId: data.find((i) => i.indexId === 'customerId')?.[type],
      vehicleId: data.find((i) => i.indexId === 'vehicleId')?.[type],
      note: data.find((i) => i.indexId === 'note')?.[type],
    };
  };

  const handleFuelFormData = async () => {
    const fuelCardDataResponse = await getFuelCardFormData();

    setValidation(handleFormData(fuelCardDataResponse, 'rules'));

    return fuelCardDataResponse || false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['fuelCardOptions'],
        queryFn: () => handleFuelFormData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { data: fuelCardData, isLoading: isLoadingFuelCardForm } = results[0];

  const findProviderName = (): string => {
    const fuelCardFilteredData = fuelCardData?.find(
      (item) => item.indexId === 'fuelCardProviderId'
    );
    if (fuelCardFilteredData?.values && watch('fuelCardProviderId')) {
      const findName = fuelCardFilteredData.values.find(
        (item) => String(item.value) === watch('fuelCardProviderId')
      );

      return findName.item;
    }
    return '';
  };

  const handleErrorResponse = (response: AxiosResponse) => {
    const responseValidation = handleFormData(response.data.form.inputs, 'rules');

    setValidation(responseValidation);

    const fields = Object.keys(getValues());
    fields.forEach((field) => {
      const filteredValidation = responseValidation[field].find(
        (item) => item.param === 'assertTrue' && !item.value
      );
      if (filteredValidation) {
        setError(field as TFuelCardsFormKeys, { message: filteredValidation.message });
        setFocus(field as TFuelCardsFormKeys, { shouldSelect: true });
      }
    });
  };

  const onSubmit = async (data) => {
    if (Object.keys(errors).length > 0) return;
    const createValues = { ...data, customerId: props.customerId };
    setIsLoaderActive(true);

    try {
      const response = await FuelCardsService.createFuelCard({ ...createValues });

      if ((response as AxiosResponse).data && (response as AxiosResponse).status === 400) {
        handleErrorResponse(response as AxiosResponse);
      } else if ((response as AxiosResponse).data && (response as AxiosResponse).status === 200) {
        const providerName = findProviderName();
        props.handleNewFuelCard({ ...(response as AxiosResponse).data.fuelCard, providerName });
        props.handleClose();
      } else {
        return;
      }
    } finally {
      setIsLoaderActive(false);
    }
    trackEvent(['vehicle form', 'click', 'Submit fuel card form']);
  };

  // const onSubmit = async (data) => {
  //   if (Object.keys(errors).length > 0) return;
  //   const createValues = { ...data, customerId: props.customerId };
  //   setIsLoaderActive(true);

  //   const response = await ApiService.post('/v1/fuel-card/add', createValues);
  //   setIsLoaderActive(false);
  //   if (response.data && response.status === 200) {
  //     const providerName = findProviderName();
  //     props.handleNewFuelCard({ ...response.data.fuelCard, providerName });
  //     props.handleClose();
  //   }
  //   props.handleClose();
  // };

  // modal adding title and buttons
  const buttons: CmdModalFooterButton[] = [
    {
      id: 'modalClose',
      type: 'button',
      title: f({ id: 'form.cancel' }),
      className: 'e-button',
      closeCallback: props.handleClose,
      tooltip: f({ id: 'form.cancel.tooltip' }),
    },
    {
      id: 'submitAddForm',
      type: 'submit',
      title: f({ id: 'form.save' }),
      className: 'e-button e-button--gray',
      submitCallback: handleSubmit(onSubmit),
      tooltip: f({ id: 'alarms.modal.recipient.save' }),
    },
  ];

  const loadingData = isLoadingFuelCardForm || isLoaderActive;

  const fuelCardFilteredData = fuelCardData?.find((item) => item.indexId === 'fuelCardProviderId');

  return (
    <CmdModal
      id="fuel-cards-in-vehicle-settings-form"
      title={f({ id: 'vehicleSettings.form.fuelCard.add' })}
      footerButtons={buttons}
      loader={loadingData}
    >
      <div>
        <CmdLabel title={f({ id: 'form.inputs.customer' })} value={props.customerName} />
        <Controller
          name="fuelCardProviderId"
          control={control}
          // rules={{
          //   required: undefined,
          // }}
          render={({ field }) => (
            <SimpleSelectWithSearchHookForm
              {...field}
              label={f({ id: 'form.inputs.fuelCards.provider' })}
              id="fuelCardProviderId"
              options={fuelCardFilteredData?.values || []}
              introOption={f({ id: 'form.choose' })}
              onChangeCallback={(value) => {
                setValue('fuelCardProviderId', value);
                trigger('fuelCardProviderId');
                trackEvent(['vehicle form', 'change', 'Select fuel card provider']);
              }}
              control={control}
              required={false}
            />
          )}
        />
        <Controller
          name="cardNumber"
          control={control}
          render={({ field }) => (
            <CmdInput
              {...field}
              id="cardNumber"
              label={f({ id: 'form.inputs.fuelCards.cardNumber' })}
              placeholder={f({ id: 'form.inputs.fuelCards.cardNumber.placeholder' })}
              type="text"
              onChangeCallback={(value) => {
                setValue('cardNumber', value);
                trigger('cardNumber');
                const findAssertTrueValidation = validation.cardNumber?.find(
                  (item) => item.param === 'assertTrue' && !item.value
                );

                if (errors.cardNumber?.message && findAssertTrueValidation) {
                  setValidation((oldValidation) => {
                    const filteredValidation = oldValidation.cardNumber.filter(
                      (item) => item.param !== 'assertTrue'
                    );

                    return {
                      ...oldValidation,
                      cardNumber: filteredValidation,
                    };
                  });

                  clearErrors('cardNumber');
                  trigger('cardNumber');
                }
              }}
              required={FormikValidatorService.isFieldRequired(
                validation ? validation.cardNumber : []
              )}
              error={errors.cardNumber?.message}
              touched={true}
            />
          )}
        />

        <Controller
          name="identifier"
          control={control}
          render={({ field }) => (
            <CmdInput
              {...field}
              id="identifier"
              label={f({ id: 'form.inputs.fuelCards.identifier' })}
              placeholder={f({ id: 'form.inputs.fuelCards.identifier.placeholder' })}
              type="text"
              onChangeCallback={(value) => {
                setValue('identifier', value);
                trigger('identifier');

                const findAssertTrueValidation = validation.identifier?.find(
                  (item) => item.param === 'assertTrue' && !item.value
                );

                if (errors.identifier?.message && findAssertTrueValidation) {
                  setValidation((oldValidation) => {
                    const filteredValidation = oldValidation.identifier.filter(
                      (item) => item.param !== 'assertTrue'
                    );

                    return {
                      ...oldValidation,
                      identifier: filteredValidation,
                    };
                  });

                  clearErrors('identifier');
                  trigger('identifier');
                }
              }}
              required={FormikValidatorService.isFieldRequired(
                validation ? validation.identifier : []
              )}
              error={errors.identifier?.message}
              touched={true}
            />
          )}
        />
        <Controller
          name="validUntil"
          control={control}
          render={({ field }) => (
            <CmdCalendar
              {...field}
              id="validUntil"
              calendarTranslations={{}}
              dateFormat="MM/yyyy"
              onChangeCallback={(value) => {
                setValue('validUntil', value as Date);
                trigger('validUntil');
                trackEvent(['vehicle form', 'change', 'Select valid until date']);
              }}
              allowedRange={[
                new Date(dayjs(new Date()).format('YYYY-MM-DD')),
                new Date(dayjs(new Date()).add(14, 'year').format('YYYY-MM-DD')),
              ]}
              label={f({ id: 'table_list.columns.fuelCards.validUntil' })}
            />
          )}
        />
        <Controller
          name="note"
          control={control}
          rules={{ required: errors.note ? errors.note.message : undefined }}
          render={({ field }) => (
            <CmdTextarea
              name={field.name}
              id="note"
              label={f({ id: 'form.inputs.note' })}
              rows={5}
              setValue={(value) => {
                setValue('note', value);
                trigger('note');
              }}
              value={field.value}
              error={errors.note?.message}
              touched={true}
            />
          )}
        />
      </div>
    </CmdModal>
  );
}
